// CREATE INDEX structure extractor. Walks the leading clause from the source
// buffer, reads the index name, the target table, and the parenthesised
// column list, and reports the v1-tracked flags (`unique`, `concurrent`).
// The partial-index `WHERE …` body (Postgres / SQLite) is stored as opaque
// `partialWhereText` per R4.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      ALTER TABLE / Index forms.

import type { ParseOptions, QualifiedName, StatementInfo } from '../../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import { readIdentifier, readQualifiedName } from '../../scope/identifiers';
import type { DdlStructure, IndexDef, ParsedBuffer } from '../../types';

import { findTopLevelCommas } from '../../structure/scanner';
import { findMatchingParen, skipWsCommentsLocal, tryConsumeKeyword } from './shared';

const INDEX_MODIFIERS: ReadonlySet<string> = new Set([
  'UNIQUE', 'CLUSTERED', 'NONCLUSTERED', 'BITMAP', 'FULLTEXT', 'SPATIAL',
]);

interface IndexHeader {
  readonly name: QualifiedName;
  readonly table: QualifiedName;
  readonly unique: boolean;
  readonly concurrent: boolean;
  readonly afterTable: number;
}

function emptyQn(): QualifiedName {
  return { name: '', raw: '', quoted: false, foldedName: '' };
}

function fallback(stmt: StatementInfo): DdlStructure {
  return {
    kind: 'create-index',
    range: stmt.range,
    index: { name: emptyQn(), table: emptyQn(), columns: [], unique: false },
  };
}

// eslint-disable-next-line complexity
function readIndexHeader(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): IndexHeader | null {
  let i = skipWsCommentsLocal(source, start, end, options);
  const afterCreate = tryConsumeKeyword(source, i, end, options, 'CREATE');
  if (afterCreate === i) return null;
  i = skipWsCommentsLocal(source, afterCreate, end, options);

  let unique = false;
  for (let iter = 0; iter < 4; iter++) {
    const lead = readIdentifier(source, i, end, options);
    if (!lead || lead.quoted) break;
    const upper = lead.value.toUpperCase();
    if (!INDEX_MODIFIERS.has(upper)) break;
    if (upper === 'UNIQUE') unique = true;
    i = skipWsCommentsLocal(source, lead.end, end, options);
  }

  const afterIndex = tryConsumeKeyword(source, i, end, options, 'INDEX');
  if (afterIndex === i) return null;
  i = skipWsCommentsLocal(source, afterIndex, end, options);

  let concurrent = false;
  const afterConc = tryConsumeKeyword(source, i, end, options, 'CONCURRENTLY');
  if (afterConc !== i) {
    concurrent = true;
    i = skipWsCommentsLocal(source, afterConc, end, options);
  }

  const afterIf = tryConsumeKeyword(source, i, end, options, 'IF');
  if (afterIf !== i) {
    let j = skipWsCommentsLocal(source, afterIf, end, options);
    j = tryConsumeKeyword(source, j, end, options, 'NOT');
    j = skipWsCommentsLocal(source, j, end, options);
    j = tryConsumeKeyword(source, j, end, options, 'EXISTS');
    j = skipWsCommentsLocal(source, j, end, options);
    i = j;
  }

  const indexName = readQualifiedName(source, i, end, options);
  if (!indexName) return null;
  i = skipWsCommentsLocal(source, indexName.range.endOffset, end, options);

  const afterOn = tryConsumeKeyword(source, i, end, options, 'ON');
  if (afterOn === i) return null;
  i = skipWsCommentsLocal(source, afterOn, end, options);

  const afterOnly = tryConsumeKeyword(source, i, end, options, 'ONLY');
  if (afterOnly !== i) i = skipWsCommentsLocal(source, afterOnly, end, options);

  const tableName = readQualifiedName(source, i, end, options);
  if (!tableName) return null;
  return {
    name: indexName.qualified,
    table: tableName.qualified,
    unique,
    concurrent,
    afterTable: tableName.range.endOffset,
  };
}

function skipUsingClause(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): number {
  let i = skipWsCommentsLocal(source, start, end, options);
  const afterUsing = tryConsumeKeyword(source, i, end, options, 'USING');
  if (afterUsing === i) return start;
  i = skipWsCommentsLocal(source, afterUsing, end, options);
  // Read the index method identifier (btree, gin, hash, …) and stop.
  const method = readIdentifier(source, i, end, options);
  if (method) return method.end;
  return i;
}

function splitColumnList(
  source: string,
  options: ParseOptions,
  contentStart: number,
  contentEnd: number,
): string[] {
  const commas = findTopLevelCommas(source, contentStart, contentEnd, options);
  const out: string[] = [];
  let segStart = contentStart;
  const push = (s: number, e: number) => {
    const a = skipWsCommentsLocal(source, s, e, options);
    let b = e;
    while (b > a) {
      const ch = source[b - 1];
      if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { b--; continue; }
      break;
    }
    if (b > a) out.push(source.slice(a, b));
  };
  for (const c of commas) { push(segStart, c); segStart = c + 1; }
  push(segStart, contentEnd);
  return out;
}

function readPartialWhere(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): string | undefined {
  const i = skipWsCommentsLocal(source, start, end, options);
  const afterWhere = tryConsumeKeyword(source, i, end, options, 'WHERE');
  if (afterWhere === i) return undefined;
  const tail = source.slice(afterWhere, end).trim();
  return tail.length > 0 ? tail : undefined;
}

export function extractCreateIndex(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const { source, options } = buffer.parsedDocument;
  const lineIdx = buildLineIndex(source);
  const stmtStart = statementInfo.range.startOffset;
  const stmtEnd = statementInfo.range.endOffset;

  const header = readIndexHeader(source, options, stmtStart, stmtEnd);
  if (!header) return fallback(statementInfo);

  // Optional `USING <method>` (Postgres / MySQL — placement varies; some
  // dialects put it before ON, others after. For v1 we only skip post-table
  // placements, which covers PG; pre-ON placements that grammar parses
  // separately are out of scope).
  const afterUsing = skipUsingClause(source, options, header.afterTable, stmtEnd);
  const afterTable = afterUsing > header.afterTable ? afterUsing : header.afterTable;

  const parenStart = skipWsCommentsLocal(source, afterTable, stmtEnd, options);
  if (source[parenStart] !== '(') return fallback(statementInfo);
  const parenEnd = findMatchingParen(source, options, parenStart, stmtEnd);
  if (parenEnd < 0) return fallback(statementInfo);

  const columns = splitColumnList(source, options, parenStart + 1, parenEnd);
  const partialWhereText = readPartialWhere(source, options, parenEnd + 1, stmtEnd);

  const index: IndexDef = {
    name: header.name,
    table: header.table,
    columns,
    unique: header.unique,
    range: rangeFromOffsets(lineIdx, stmtStart, stmtEnd),
    ...(header.concurrent ? { concurrent: true } : {}),
    ...(partialWhereText ? { partialWhereText } : {}),
  };

  return { kind: 'create-index', range: statementInfo.range, index };
}
